import type { PaymentSnapshot } from '../payment/payment-authority.ts';
import { summarizeRequiredFlow, type FulfillmentTask } from './required-flow.ts';

export type FulfillmentState =
  | 'held'
  | 'production_authorized'
  | 'ready'
  | 'completed'
  | 'cancelled';

export interface FulfillmentLine {
  readonly lineId: string;
  readonly productId: string;
  readonly quantity: number;
}

export interface FulfillmentOrderTerms {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly requiresPaymentBeforeProduction: boolean;
  readonly requiredTasks: readonly FulfillmentTask[];
  readonly lines: readonly FulfillmentLine[];
}

export interface FulfillmentMutationContext {
  readonly writer: string;
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
}

export interface RecoveryReasonContext extends FulfillmentMutationContext {
  readonly reason: string;
}

export interface ReopenContext extends RecoveryReasonContext {
  readonly supervisorAuthorized: boolean;
}

export interface FulfillmentAuditEntry {
  readonly auditId: string;
  readonly action:
    | 'production_authorized'
    | 'ready'
    | 'completed'
    | 'reopened'
    | 'line_voided'
    | 'cancelled';
  readonly version: number;
  readonly operatorId: string;
  readonly at: string;
  readonly idempotencyKey: string;
  readonly lineId?: string;
  readonly reason?: string;
}

export interface FulfillmentSnapshot {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly state: FulfillmentState;
  readonly version: number;
  readonly requiresPaymentBeforeProduction: boolean;
  readonly requiredTasks: readonly FulfillmentTask[];
  readonly lines: readonly FulfillmentLine[];
  readonly voidedLineIds: readonly string[];
}

export interface SmtFulfillmentProjection {
  readonly active: boolean;
  readonly stage: 'open' | 'ready' | 'completed' | 'cancelled';
}

export interface QuickDuplicateProposal {
  readonly sourceOrderId: string;
  readonly sourceOrderRevision: number;
  readonly lines: readonly FulfillmentLine[];
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: FulfillmentSnapshot;
}

interface FulfillmentAccount {
  readonly terms: FulfillmentOrderTerms;
  state: FulfillmentState;
  version: number;
  readonly voidedLineIds: Set<string>;
  readonly audit: FulfillmentAuditEntry[];
  readonly idempotency: Map<string, IdempotentResult>;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (kind: string, command: unknown): string =>
  JSON.stringify(stableValue({ kind, command }));

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const validateTerms = (input: FulfillmentOrderTerms): FulfillmentOrderTerms => {
  assertText(input.orderId, 'FULFILLMENT_ORDER_ID_REQUIRED');
  if (!Number.isSafeInteger(input.orderRevision) || input.orderRevision <= 0) {
    throw new Error('FULFILLMENT_ORDER_REVISION_INVALID');
  }
  summarizeRequiredFlow(input.requiredTasks);
  if (input.lines.length === 0) throw new Error('FULFILLMENT_LINES_REQUIRED');
  const seen = new Set<string>();
  for (const line of input.lines) {
    assertText(line.lineId, 'FULFILLMENT_LINE_ID_REQUIRED');
    assertText(line.productId, 'FULFILLMENT_PRODUCT_ID_REQUIRED');
    if (seen.has(line.lineId)) throw new Error('FULFILLMENT_LINE_DUPLICATE');
    seen.add(line.lineId);
    if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) {
      throw new Error('FULFILLMENT_LINE_QUANTITY_INVALID');
    }
  }
  return clone(input);
};

const termsSignature = (terms: FulfillmentOrderTerms): string =>
  JSON.stringify(stableValue(terms));

export function projectSmtFulfillment(snapshot: FulfillmentSnapshot): SmtFulfillmentProjection {
  if (snapshot.state === 'held' || snapshot.state === 'production_authorized') {
    return { active: true, stage: 'open' };
  }
  if (snapshot.state === 'ready') return { active: true, stage: 'ready' };
  if (snapshot.state === 'completed') return { active: false, stage: 'completed' };
  return { active: false, stage: 'cancelled' };
}

export function createFulfillmentAuthority() {
  const accounts = new Map<string, FulfillmentAccount>();

  const get = (orderId: string): FulfillmentAccount => {
    const account = accounts.get(orderId);
    if (!account) throw new Error('FULFILLMENT_ACCOUNT_NOT_FOUND');
    return account;
  };

  const snapshotFor = (account: FulfillmentAccount): FulfillmentSnapshot => ({
    orderId: account.terms.orderId,
    orderRevision: account.terms.orderRevision,
    state: account.state,
    version: account.version,
    requiresPaymentBeforeProduction: account.terms.requiresPaymentBeforeProduction,
    requiredTasks: account.terms.requiredTasks.map((task) => ({ ...task })),
    lines: account.terms.lines.map((line) => ({ ...line })),
    voidedLineIds: [...account.voidedLineIds].sort(),
  });

  const assertContext = (command: FulfillmentMutationContext): void => {
    if (command.writer !== 'smt-local') throw new Error('FULFILLMENT_WRITER_NOT_ALLOWED');
    if (!Number.isSafeInteger(command.expectedVersion) || command.expectedVersion < 0) {
      throw new Error('FULFILLMENT_EXPECTED_VERSION_INVALID');
    }
    assertText(command.idempotencyKey, 'FULFILLMENT_IDEMPOTENCY_KEY_REQUIRED');
    assertText(command.operatorId, 'FULFILLMENT_OPERATOR_REQUIRED');
    if (!Number.isFinite(Date.parse(command.at))) throw new Error('FULFILLMENT_TIMESTAMP_INVALID');
  };

  const appendAudit = (
    account: FulfillmentAccount,
    command: FulfillmentMutationContext,
    action: FulfillmentAuditEntry['action'],
    extra: Pick<FulfillmentAuditEntry, 'lineId' | 'reason'> = {},
  ): void => {
    account.audit.push({
      auditId: `${account.terms.orderId}:fulfillment:${account.version + 1}:${command.idempotencyKey}:${action}`,
      action,
      version: account.version + 1,
      operatorId: command.operatorId,
      at: command.at,
      idempotencyKey: command.idempotencyKey,
      ...extra,
    });
  };

  const mutate = (
    account: FulfillmentAccount,
    kind: string,
    command: FulfillmentMutationContext,
    action: () => void,
  ): FulfillmentSnapshot => {
    assertContext(command);
    const signature = signatureOf(kind, command);
    const replay = account.idempotency.get(command.idempotencyKey);
    if (replay) {
      if (replay.signature !== signature) throw new Error('FULFILLMENT_IDEMPOTENCY_CONFLICT');
      return clone(replay.result);
    }
    if (command.expectedVersion !== account.version) throw new Error('FULFILLMENT_VERSION_CONFLICT');

    action();
    account.version += 1;
    const result = snapshotFor(account);
    account.idempotency.set(command.idempotencyKey, { signature, result: clone(result) });
    return clone(result);
  };

  const assertReason = (reason: string): void => {
    if (!reason?.trim()) throw new Error('FULFILLMENT_REASON_REQUIRED');
  };

  return {
    open(input: FulfillmentOrderTerms): FulfillmentSnapshot {
      const accepted = validateTerms(input);
      const existing = accounts.get(accepted.orderId);
      if (existing) {
        if (termsSignature(existing.terms) !== termsSignature(accepted)) {
          throw new Error('FULFILLMENT_ORDER_TERMS_CONFLICT');
        }
        return clone(snapshotFor(existing));
      }
      const account: FulfillmentAccount = {
        terms: accepted,
        state: 'held',
        version: 0,
        voidedLineIds: new Set(),
        audit: [],
        idempotency: new Map(),
      };
      accounts.set(accepted.orderId, account);
      return clone(snapshotFor(account));
    },

    snapshot(orderId: string): FulfillmentSnapshot {
      return clone(snapshotFor(get(orderId)));
    },

    audit(orderId: string): FulfillmentAuditEntry[] {
      return get(orderId).audit.map((entry) => ({ ...entry }));
    },

    authorizeProduction(
      orderId: string,
      command: FulfillmentMutationContext,
      payment?: Pick<PaymentSnapshot, 'orderId' | 'orderRevision' | 'fullySettled'>,
    ): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, 'authorize_production', command, () => {
        if (account.state !== 'held') throw new Error('FULFILLMENT_TRANSITION_INVALID');
        if (!summarizeRequiredFlow(account.terms.requiredTasks).canCheckout) {
          throw new Error('FULFILLMENT_REQUIRED_FLOW_INCOMPLETE');
        }
        if (account.terms.requiresPaymentBeforeProduction) {
          if (!payment) throw new Error('FULFILLMENT_PAYMENT_REQUIRED');
          if (payment.orderId !== account.terms.orderId) throw new Error('FULFILLMENT_PAYMENT_ORDER_MISMATCH');
          if (payment.orderRevision !== account.terms.orderRevision) throw new Error('FULFILLMENT_PAYMENT_REVISION_MISMATCH');
          if (!payment.fullySettled) throw new Error('FULFILLMENT_PAYMENT_REQUIRED');
        }
        appendAudit(account, command, 'production_authorized');
        account.state = 'production_authorized';
      });
    },

    markReady(orderId: string, command: FulfillmentMutationContext): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, 'mark_ready', command, () => {
        if (account.state !== 'production_authorized') throw new Error('FULFILLMENT_TRANSITION_INVALID');
        appendAudit(account, command, 'ready');
        account.state = 'ready';
      });
    },

    markCompleted(orderId: string, command: FulfillmentMutationContext): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, 'mark_completed', command, () => {
        if (account.state !== 'ready') throw new Error('FULFILLMENT_TRANSITION_INVALID');
        appendAudit(account, command, 'completed');
        account.state = 'completed';
      });
    },

    reopenCompleted(orderId: string, command: ReopenContext): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, 'reopen_completed', command, () => {
        if (account.state !== 'completed') throw new Error('FULFILLMENT_TRANSITION_INVALID');
        if (!command.supervisorAuthorized) throw new Error('FULFILLMENT_SUPERVISOR_REQUIRED');
        assertReason(command.reason);
        appendAudit(account, command, 'reopened', { reason: command.reason.trim() });
        account.state = 'ready';
      });
    },

    voidLine(orderId: string, lineId: string, command: RecoveryReasonContext): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, `void_line:${lineId}`, command, () => {
        if (account.state === 'cancelled') throw new Error('FULFILLMENT_TRANSITION_INVALID');
        assertReason(command.reason);
        if (!account.terms.lines.some((line) => line.lineId === lineId)) throw new Error('FULFILLMENT_LINE_NOT_FOUND');
        if (account.voidedLineIds.has(lineId)) throw new Error('FULFILLMENT_LINE_ALREADY_VOIDED');
        account.voidedLineIds.add(lineId);
        appendAudit(account, command, 'line_voided', { lineId, reason: command.reason.trim() });
      });
    },

    quickDuplicateProposal(orderId: string): QuickDuplicateProposal {
      const account = get(orderId);
      return {
        sourceOrderId: account.terms.orderId,
        sourceOrderRevision: account.terms.orderRevision,
        lines: account.terms.lines
          .filter((line) => !account.voidedLineIds.has(line.lineId))
          .map((line) => ({ ...line })),
      };
    },

    cancel(orderId: string, command: RecoveryReasonContext): FulfillmentSnapshot {
      const account = get(orderId);
      return mutate(account, 'cancel', command, () => {
        if (account.state === 'completed' || account.state === 'cancelled') {
          throw new Error('FULFILLMENT_TRANSITION_INVALID');
        }
        assertReason(command.reason);
        appendAudit(account, command, 'cancelled', { reason: command.reason.trim() });
        account.state = 'cancelled';
      });
    },
  };
}
